import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Menu, Search, Share } from "lucide-react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function NavMenuContent({
  menuOpen,
  setMenuOpen
}: {
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [searchOpen, setSearchOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);
  return <div className="bg-background text-foreground border-b border-border relative" style={{
    fontFamily: "'Inter', sans-serif"
  }}>
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
      backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 39px, hsl(var(--border)) 39px, hsl(var(--border)) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, hsl(var(--border)) 39px, hsl(var(--border)) 40px)"
    }} />

      <div className="relative max-w-screen-xl mx-auto px-6">
        <div className="flex items-stretch min-h-[72px]">

          <div className="flex items-center pr-8 border-r border-border mr-8 flex-shrink-0">
            <Link to="/" className="block group">
              <WvcLogo className="h-7 transition-opacity duration-300 group-hover:opacity-75" />
            </Link>
          </div>

          <nav className="hidden md:flex items-stretch flex-1">
            {!loading && menuItems.map((item, i) => <Link key={item.id || i} to={item.href || "#"} className="relative flex items-center px-5 text-xs tracking-widest font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 group" style={{
            fontFamily: "'Share Tech Mono', monospace"
          }}>
                <span className="absolute top-0 left-0 right-0 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                <span className="mr-2 text-primary opacity-40 text-[10px]" style={{
              fontFamily: "'Share Tech Mono', monospace"
            }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                {item.label}
              </Link>)}
          </nav>

          <div className="hidden md:flex items-center gap-1 ml-auto border-l border-border pl-4">

            <div ref={searchRef} className="relative">
              <button onClick={() => setSearchOpen(v => !v)} className="w-10 h-10 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200" aria-label="Search">
                <Search className="w-4 h-4" />
              </button>
              {searchOpen && <div className="absolute right-0 top-full mt-1 w-64 bg-card border border-border shadow-xl z-50 p-3">
                  <Input autoFocus placeholder="Search..." className="bg-background border-border text-foreground placeholder:text-muted-foreground text-sm h-8" style={{
                fontFamily: "'Share Tech Mono', monospace"
              }} />
                </div>}
            </div>

            <div ref={langRef} className="relative">
              <button onClick={() => setLangOpen(v => !v)} className="flex items-center gap-1.5 px-3 h-10 text-xs tracking-widest font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200 rounded-sm" style={{
              fontFamily: "'Share Tech Mono', monospace"
            }}>
                <span>EN</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`}>
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              {langOpen && <div className="absolute right-0 top-full mt-1 w-28 bg-card border border-border shadow-xl z-50 overflow-hidden">
                  {["EN", "ZH", "JA"].map(lang => <button key={lang} className="w-full flex items-center px-4 py-2.5 text-xs tracking-widest text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150" style={{
                fontFamily: "'Share Tech Mono', monospace"
              }}>
                      {lang}
                    </button>)}
                </div>}
            </div>
          </div>

          <button className="md:hidden ml-auto flex items-center justify-center w-10 h-10 self-center text-muted-foreground hover:text-foreground transition-colors duration-200" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            {menuOpen ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && <div className="md:hidden bg-card border-t border-border relative z-40">
          <nav className="flex flex-col divide-y divide-border">
            {!loading && menuItems.map((item, i) => <Link key={item.id || i} to={item.href || "#"} onClick={() => setMenuOpen(false)} className="flex items-center gap-4 px-6 py-4 text-xs tracking-widest text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200" style={{
          fontFamily: "'Share Tech Mono', monospace"
        }}>
                <span className="text-primary text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                {item.label}
              </Link>)}
            <div className="flex items-center gap-2 px-6 py-4">
              <Input placeholder="Search..." className="bg-background border-border text-foreground placeholder:text-muted-foreground text-sm h-8 flex-1" style={{
            fontFamily: "'Share Tech Mono', monospace"
          }} />
              <button className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground">
                <Search className="w-3.5 h-3.5" />
              </button>
            </div>
          </nav>
        </div>}
    </div>;
}
export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  return <header id="header" className="dark">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Inter:wght@300;400;500;700&display=swap');`}</style>
      <MenuProvider menu_id="16">
        <NavMenuContent menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      </MenuProvider>
    </header>;
}